import { useEffect, useState } from 'react'
import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../stores/auth'
import { api } from '../../lib/api'
import { clearPushToken } from '../../lib/fcm'
import { NetworkStatusBanner } from '../NetworkStatusBanner'
import { DemoBanner } from '../DemoBanner'

function ImpersonationBanner() {
  const { impersonatedBy, stopImpersonation } = useAuthStore()
  const navigate = useNavigate()

  if (!impersonatedBy) return null

  const handleExit = () => {
    stopImpersonation()
    navigate('/owner/managers')
  }

  return (
    <div className="bg-orange-500 text-white px-4 py-2 flex items-center justify-between gap-3 text-sm">
      <span className="truncate">
        👤 Вы работаете как менеджер. Владелец: <strong>{impersonatedBy.user.name}</strong>
      </span>
      <button
        onClick={handleExit}
        className="shrink-0 bg-white text-orange-600 font-semibold px-3 py-1 rounded-lg text-xs hover:bg-orange-50"
      >
        Выйти
      </button>
    </div>
  )
}

const navItems = [
  { to: '/manager', label: 'Смена', icon: '⏱️', end: true },
  { to: '/manager/scan', label: 'Сканировать', icon: '📷' },
  { to: '/manager/rentals', label: 'Аренды', icon: '🚲' },
  { to: '/manager/profile', label: 'Профиль', icon: '👤' },
]

export default function ManagerLayout() {
  const { user, logout } = useAuthStore()
  const navigate = useNavigate()
  const [loggingOut, setLoggingOut] = useState(false)

  // Request geolocation permission early — Android shows the dialog on first call
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        () => {}, // success — permission granted
        () => {}, // denied or error — silently ignore here
        { timeout: 5000, maximumAge: 60_000 },
      )
    }
  }, [])

  const handleLogout = async () => {
    if (loggingOut) return
    setLoggingOut(true)
    const deadline = new Promise<void>((resolve) => setTimeout(resolve, 2000))
    await Promise.race([clearPushToken().catch(() => {}), deadline])
    try { await Promise.race([api.post('/auth/logout'), deadline]) } catch {}
    logout()
    navigate('/login')
  }

  return (
    <div className="h-[100dvh] overflow-hidden flex flex-col bg-gray-50">
      {/* Header */}
      <header className="shrink-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between safe-top">
        <div>
          <h1 className="text-base font-bold text-brand-600">VeloRent</h1>
          <p className="text-xs text-gray-500">{user?.name}</p>
        </div>
        <button
          onClick={handleLogout}
          disabled={loggingOut}
          className="text-sm text-gray-500 hover:text-red-600 disabled:opacity-60 px-4 py-2 rounded-lg min-w-[64px] active:bg-red-50"
        >
          {loggingOut ? '...' : 'Выйти'}
        </button>
      </header>

      {/* Impersonation banner */}
      <ImpersonationBanner />

      {/* Offline banner */}
      <NetworkStatusBanner />

      {/* Demo tenant warning banner */}
      <DemoBanner />

      {/* Content */}
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>

      {/* Bottom nav */}
      <nav className="shrink-0 bg-white border-t border-gray-200 flex safe-bottom">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center gap-1 py-2 text-xs transition-colors ${
                isActive ? 'text-brand-600 font-medium' : 'text-gray-500'
              }`
            }
          >
            <span className="text-xl">{item.icon}</span>
            {item.label}
          </NavLink>
        ))}
      </nav>
    </div>
  )
}
